'use client'
import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { Heading, Picture, Text } from '../../components/index.js'
import { cn } from '../../helpers/utils.js'
import { withLayout } from '../../shared/Styles.js'
import { useExposure } from '../../hooks/useExposure.js'
import type { TabsWithMediaProps, TabItem, TimeIndex } from './types.js'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination, EffectFade } from 'swiper/modules'
import type { Swiper as SwiperType } from 'swiper'
import { motion, AnimatePresence } from 'framer-motion'
import { useMediaQuery } from 'react-responsive'

const componentType = 'video'
const componentName = 'tabs_with_media'

// 默认时间点配置
const defaultTimeIdx: TimeIndex[] = [
  { time: 2.4, point: 2.4, highlightIdx: 0 },
  { time: 2.5, point: 4.9, highlightIdx: 1 },
  { time: 2.5, point: 7.4, highlightIdx: 2 },
  { time: 2.8, point: 10.2, highlightIdx: 3 },
  { time: 2.6, point: 12.8, highlightIdx: 4 },
  { time: 3.2, point: 16, highlightIdx: 5 },
]

const TabsWithMedia = React.forwardRef<HTMLDivElement, TabsWithMediaProps>(({ data, className }, ref) => {
  const { title, poster, video, mobvideo, items = [], timeIdx = defaultTimeIdx, theme = 'light' } = data

  const [swiper, setSwiper] = useState<SwiperType | null>(null)
  const tabRefs = useRef<(HTMLDivElement | null)[]>([])
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const boxRef = useRef<HTMLDivElement>(null)

  useExposure(boxRef, {
    componentType,
    componentName,
    componentTitle: title,
  })

  useImperativeHandle(ref, () => boxRef.current as HTMLDivElement)

  const scrollToTabCenter = (index: number) => {
    const container = scrollContainerRef.current
    const tab = tabRefs.current[index]

    if (container && tab) {
      const containerRect = container.getBoundingClientRect()
      const tabRect = tab.getBoundingClientRect()
      const offset = tabRect.left - containerRect.left
      const scroll = offset - container.clientWidth / 2 + tab.clientWidth / 2

      container.scrollTo({
        left: container.scrollLeft + scroll,
        behavior: 'smooth',
      })
    }
  }

  const isMob = useMediaQuery({ query: '(max-width: 768px)' })

  const [list, listDouble, row, center, listLength] = useMemo(() => {
    const row = 5
    const center = 2
    const listLength = items.length

    const list =
      items?.map((item: TabItem, index: number) => {
        return {
          ...item,
          key: index,
          index: index,
        }
      }) || []

    // 为第二个列表的元素创建唯一的 key,避免重复
    const listSecond =
      items?.map((item: TabItem, index: number) => {
        return {
          ...item,
          key: index + listLength,
          index: index,
        }
      }) || []

    return [list, list.concat(listSecond), row, center, listLength]
  }, [items, isMob])

  const [idx, setIdx] = useState(center)

  const [cureentList, setCureentList] = useState(listDouble.slice(0, row))

  // 点击 tab 跳转对应视频位置
  const handleNavClick = (key: number, index: number, isAuto?: boolean) => {
    if (key === idx) return
    if (index === center) return
    if (index > center) {
      const gap = index - center
      const copy = [...cureentList]
      copy.splice(0, gap)
      const lastkey = (cureentList[cureentList.length - 1] as any)?.key + 1
      const add = [...listDouble].splice(lastkey, gap)
      copy.push(...add)
      setCureentList([...copy])
    }
    if (index < center) {
      const gap = center - index
      const copy = [...cureentList]
      copy.splice(-gap)
      const firstkey = (cureentList[0] as any)?.key
      const add = [...listDouble].splice(listLength + firstkey - gap, gap)
      copy.unshift(...add)
      setCureentList([...copy])
    }

    setIdx(key)
    swiper?.slideTo(key)

    if (!isAuto) {
      let time = 0
      timeIdx.forEach(item => {
        if (item.highlightIdx < key) {
          time += item.time
        }
      })
      if (videoRef.current) {
        videoRef.current.currentTime = time
      }
    }
  }

  // 播放时根据时间切换 Swiper + tab
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const handleTimeUpdate = () => {
      const currentTime = video.currentTime

      // 自动切换内容
      for (let i = 0; i < timeIdx.length; i++) {
        const prev = i === 0 ? 0 : timeIdx[i - 1].point
        if (currentTime >= prev && currentTime < timeIdx[i].point) {
          handleNavClick(timeIdx[i].highlightIdx, timeIdx[i].highlightIdx, true)
          break
        }
      }

      // 修复：播放到最后后自动归零并重新播放
      if (currentTime >= timeIdx[timeIdx.length - 1].point) {
        video.currentTime = 0
        video.play()
      }
    }

    video.addEventListener('timeupdate', handleTimeUpdate)
    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate)
    }
  }, [swiper, timeIdx])

  return (
    <section
      ref={boxRef}
      data-ui-component-id="TabsWithMedia"
      className={cn(
        'tabs-with-media text-info-primary flex flex-col overflow-hidden sm:overflow-visible',
        {
          'aiui-dark': theme === 'dark',
        },
        className
      )}
    >
      <Heading as="h3" size={4} html={title} className="tabs-with-media__title text-center" />
      <div className="tabs-with-media__tabs-wrapper scrollbar-hidden relative w-full overflow-x-auto sm:overflow-visible">
        <div className={cn('tabs-with-media__tabs-inner tablet:w-fit mx-auto mt-[16px] w-full max-w-[824px]')}>
          <div
            className={cn(
              'tabs-with-media__tabs-container flex translate-x-0 items-center justify-center gap-[16px] transition-all md:gap-[8px]'
            )}
            ref={scrollContainerRef}
          >
            <AnimatePresence mode="popLayout">
              {cureentList?.map((item: any, index: number) => (
                <motion.div
                  layout
                  key={item?.key}
                  ref={el => {
                    tabRefs.current[item?.index] = el
                  }}
                  onClick={() => {
                    handleNavClick(item?.key, index)
                  }}
                  className="tabs-with-media__tab-item min-w-[28px] flex-none cursor-pointer overflow-hidden sm:overflow-visible"
                  initial={{ opacity: 1 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{
                    duration: 0.3,
                    ease: 'easeOut',
                  }}
                >
                  <div className="tabs-with-media__tab-inner flex items-center justify-between">
                    <div
                      className={cn('tabs-with-media__tab-content flex', {
                        'flex items-center justify-center gap-[8px] rounded-btn bg-[#fff] p-2 md:gap-[4px] md:p-1':
                          idx === item?.key,
                      })}
                    >
                      <div
                        className={cn(
                          'tabs-with-media__tab-icon lg-desktop:size-[48px] flex size-[28] items-center justify-center rounded-full md:shrink-0',
                          {
                            'h-[28px] w-[28px] bg-[#ccc] bg-opacity-50 lg-desktop:h-[48px] lg-desktop:w-[48px]':
                              idx !== item?.index,
                          }
                        )}
                        style={
                          idx === item?.key
                            ? {
                                background: 'var(--1, linear-gradient(90deg, #3AD1FF 0%, #008CD6 100%))',
                              }
                            : undefined
                        }
                      >
                        <Picture
                          source={item?.icon?.url}
                          alt={item?.icon?.alt}
                          className="tabs-with-media__tab-icon-image size-[22px] md:size-[14px]"
                        />
                      </div>

                      <div className="tabs-with-media__tab-text-wrapper">
                        <Heading
                          as="h4"
                          size={2}
                          html={item?.tab}
                          className={cn(
                            'tabs-with-media__tab-text dynamic-width-heading xs:text-[16px] line-clamp-1 size-0 overflow-hidden whitespace-nowrap text-nowrap text-black opacity-0 transition-all duration-300 ease-linear sm:text-[18px]',
                            {
                              'h-auto w-[calc-size(auto,size)] px-[6px] opacity-100': idx === item?.key,
                            }
                          )}
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </div>
      <div className="tabs-with-media__description-container lg-desktop:mt-[24px] mt-[16px]">
        <Swiper
          slidesPerView={1}
          effect="fade"
          fadeEffect={{ crossFade: true }}
          modules={[Pagination, EffectFade]}
          onSlideChange={swiper => {
            const newIndex = swiper.realIndex
            setIdx(newIndex)
          }}
          onSwiper={swiper => setSwiper(swiper)}
          className="tabs-with-media__description-swiper"
        >
          {items.map((it: TabItem, ind: number) => (
            <SwiperSlide
              key={ind}
              style={{ transition: 'opacity 1s ease-in-out' }}
              className="tabs-with-media__description-slide rounded-btn"
            >
              <div className="tabs-with-media__description-content">
                <Text
                  as="p"
                  size={2}
                  html={it.desc}
                  className="tabs-with-media__description-text desktop:text-[20px] lg-desktop:text-[18px] text-center"
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
      <div className="tabs-with-media__video-wrapper rounded-box laptop:mt-[32px] desktop:mt-[40px] lg-desktop:mt-[64px] mt-[24px] overflow-hidden">
        <video
          className="tabs-with-media__video desktop:h-[448px] lg-desktop:h-[560px] mx-auto h-[360px] w-fit"
          src={isMob ? mobvideo?.url : video?.url}
          poster={poster?.url}
          playsInline
          autoPlay
          muted
          loop
          ref={videoRef}
        />
      </div>
    </section>
  )
})

TabsWithMedia.displayName = 'TabsWithMedia'

export default withLayout(TabsWithMedia)
